/**
 * Program RAG Search Component
 *
 * A semantic search component specifically for finding programs using RAG.
 * Provides natural language search across program descriptions, services,
 * eligibility criteria, and other program content.
 *
 * Features:
 * - Semantic search using RAG embeddings
 * - Typeahead suggestions
 * - Similar program recommendations
 * - Search filters by domain (Housing, Food, Healthcare, etc.)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Search,
  X,
  Sparkles,
  Building2,
  Loader2,
  AlertCircle,
  ChevronDown,
  Zap,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';

// Types
export interface ProgramSearchResult {
  id: string;
  document_id: string;
  program_id?: number;
  program_name: string;
  description?: string;
  program_type?: string;
  score: number;
  metadata: {
    cbo_id?: string;
    source_entity_id?: string;
    funding_source?: string;
    services_offered?: string[];
    eligibility_summary?: string;
    [key: string]: any;
  };
}

interface ProgramRAGSearchProps {
  onResults?: (results: ProgramSearchResult[]) => void;
  onProgramSelect?: (programId: number) => void;
  onSearchStateChange?: (isSearching: boolean) => void;
  placeholder?: string;
  showSuggestions?: boolean;
  showSimilarPrograms?: boolean;
  className?: string;
  autoFocus?: boolean;
  domainFilter?: string;
}

// Domain/SDOH categories for filtering
const DOMAIN_FILTERS = [
  { value: '', label: 'All Programs' },
  { value: 'housing', label: 'Housing' },
  { value: 'food', label: 'Food & Nutrition' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'employment', label: 'Employment' },
  { value: 'education', label: 'Education' },
  { value: 'benefits', label: 'Benefits Navigation' },
  { value: 'mental_health', label: 'Mental Health' },
  { value: 'substance_use', label: 'Substance Use' },
];

const ProgramRAGSearch: React.FC<ProgramRAGSearchProps> = ({
  onResults,
  onProgramSelect,
  onSearchStateChange,
  placeholder = "Search programs by description, services, eligibility...",
  showSuggestions = true,
  showSimilarPrograms = false,
  className = '',
  autoFocus = false,
  domainFilter: initialDomainFilter = ''
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProgramSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [domainFilter, setDomainFilter] = useState(initialDomainFilter);
  const [showDomainDropdown, setShowDomainDropdown] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Click outside to close results
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
        setShowDomainDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const performSearch = useCallback(async (searchQuery: string, domain: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      onResults?.([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    onSearchStateChange?.(true);

    try {
      // Build the search query with domain context
      const enhancedQuery = domain
        ? `${domain} programs: ${searchQuery}`
        : searchQuery;

      const response = await apiClient.post('/api/rag/search', {
        query: enhancedQuery,
        top_k: 10,
        include_metadata: true,
        document_types: ['program'] // Only search programs
      });

      const searchResults = (response.data?.results || response.data?.matches || [])
        .map((result: any) => ({
          id: result.id || result.document_id,
          document_id: result.document_id,
          program_id: result.metadata?.source_entity_id
            ? parseInt(result.metadata.source_entity_id)
            : undefined,
          program_name: result.title || result.metadata?.title || 'Unknown Program',
          description: result.content_preview || result.metadata?.description,
          program_type: result.metadata?.program_type,
          score: result.score,
          metadata: result.metadata || {}
        }));

      setResults(searchResults);
      onResults?.(searchResults);
      setShowResults(true);
    } catch (err: any) {
      console.error('Program RAG search error:', err);
      setError(err.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setIsLoading(false);
      onSearchStateChange?.(false);
    }
  }, [onResults, onSearchStateChange]);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    if (value.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value, domainFilter);
      }, 300);
    } else {
      setResults([]);
      setShowResults(false);
    }
  };

  // Handle domain filter change
  const handleDomainChange = (domain: string) => {
    setDomainFilter(domain);
    setShowDomainDropdown(false);
    if (query.trim().length >= 2) {
      performSearch(query, domain);
    }
  };

  // Handle result selection
  const handleResultClick = (result: ProgramSearchResult) => {
    if (result.program_id && onProgramSelect) {
      onProgramSelect(result.program_id);
    }
    setShowResults(false);
  };

  // Clear search
  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    setError(null);
    onResults?.([]);
    inputRef.current?.focus();
  };

  // Find similar programs
  const findSimilarPrograms = async (programId: number) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/api/rag/search', {
        query: `Find programs similar to program ID ${programId}`,
        top_k: 5,
        include_metadata: true,
        document_types: ['program']
      });

      const similarResults = (response.data?.results || response.data?.matches || [])
        .filter((r: any) => r.metadata?.source_entity_id !== String(programId))
        .map((result: any) => ({
          id: result.id || result.document_id,
          document_id: result.document_id,
          program_id: result.metadata?.source_entity_id
            ? parseInt(result.metadata.source_entity_id)
            : undefined,
          program_name: result.title || result.metadata?.title || 'Unknown Program',
          description: result.content_preview,
          program_type: result.metadata?.program_type,
          score: result.score,
          metadata: result.metadata || {}
        }));

      setResults(similarResults);
      setShowResults(true);
    } catch (err) {
      console.error('Similar programs search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Quick suggestions
  const suggestions = showSuggestions ? [
    "Housing assistance programs",
    "Food insecurity help",
    "Mental health services",
    "Transportation support",
    "Employment training",
    "Benefits enrollment help"
  ] : [];

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Search Input Container */}
      <div className="flex items-center gap-2">
        {/* Domain Filter Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDomainDropdown(!showDomainDropdown)}
            className="flex items-center gap-2 px-3 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors min-w-[140px]"
          >
            <Building2 className="h-4 w-4 text-gray-500" />
            <span className="truncate">
              {DOMAIN_FILTERS.find(d => d.value === domainFilter)?.label || 'All Programs'}
            </span>
            <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${showDomainDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showDomainDropdown && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
              {DOMAIN_FILTERS.map((domain) => (
                <button
                  key={domain.value}
                  onClick={() => handleDomainChange(domain.value)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    domainFilter === domain.value
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {domain.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Search Input */}
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
            ) : (
              <Search className="h-5 w-5" />
            )}
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query.trim().length >= 2 && setShowResults(true)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="w-full pl-10 pr-20 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <span className="flex items-center gap-1 px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs rounded-md">
              <Sparkles className="h-3 w-3" />
              RAG
            </span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Suggestions (when no query) */}
      {showSuggestions && !query && !showResults && (
        <div className="mt-2 flex flex-wrap gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Try:</span>
          {suggestions.slice(0, 4).map((suggestion, index) => (
            <button
              key={index}
              onClick={() => {
                setQuery(suggestion);
                performSearch(suggestion, domainFilter);
              }}
              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Search Results Dropdown */}
      {showResults && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-30 max-h-96 overflow-y-auto">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {results.length} program{results.length !== 1 ? 's' : ''} found
            </span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {results.map((result) => (
              <button
                key={result.id}
                onClick={() => handleResultClick(result)}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                    <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {result.program_name}
                      </span>
                      {result.program_type && (
                        <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">
                          {result.program_type.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    {result.description && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {result.description}
                      </p>
                    )}
                    <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3" />
                        {Math.round(result.score * 100)}% match
                      </span>
                      {result.metadata?.funding_source && (
                        <span>Funded by: {result.metadata.funding_source}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-2" />
                </div>
              </button>
            ))}
          </div>

          {/* Find Similar Programs Option */}
          {showSimilarPrograms && results.length > 0 && results[0].program_id && (
            <div className="p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={() => findSimilarPrograms(results[0].program_id!)}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Find similar to "{results[0].program_name}"
              </button>
            </div>
          )}
        </div>
      )}

      {/* No Results Message */}
      {showResults && query && results.length === 0 && !isLoading && !error && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-30 p-6 text-center">
          <Search className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-400">
            No programs found for "{query}"
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            Try different keywords or broaden your search
          </p>
        </div>
      )}
    </div>
  );
};

export default ProgramRAGSearch;
